import React from 'react';
import { motion } from 'framer-motion';
import { Briefcase, TrendingUp, Users, Target, Award, Building2, Zap } from 'lucide-react';
export function EmployersPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-509" data-mp="y11ljd9x16z4">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-510" data-mp="y3w93do877wr">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-511" data-mp="132h6z6g09fi1">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-512" data-mp="fd50wffz5njy">
            <span className="w-8 h-px bg-teal" data-id="element-513" data-mp="kb6guri3t6r7"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-514" data-mp="1qq7hvblzkf4n">
              Applications
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-515" data-mp="1voio7cd2o7i4"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-516" data-mp="4s5z4m27omju">
            Employers and Workforce Readiness
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-517" data-mp="ogda4cx8j2i">
            SyNeural's platform architecture is designed to help employers build
            stronger teams, more capable professionals, and more reliable
            leadership pipelines.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-518" data-mp="3d7povb3obmu5">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-519" data-mp="3964d5t2vlelj">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-520" data-mp="2u8lgm4jyhx3j">
            Through CJI and NCJI, the platform provides a structured framework
            for understanding judgment, identifying readiness gaps, and
            connecting those gaps to meaningful development pathways that
            support stronger performance over time.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-521" data-mp="2bmw4j1xtldil">
            In workforce environments, success depends on more than technical
            skill alone. Organizations rely on people who can think clearly,
            communicate well, regulate themselves under pressure, follow through
            consistently, adapt to complexity, and grow into larger
            responsibilities. SyNeural helps make those capabilities more
            visible and more actionable, creating a stronger foundation for
            workforce development, advancement, and organizational resilience.
          </p>
        </motion.div>
      </section>

      {/* Application Areas */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto space-y-20" data-id="element-522" data-mp="3r34omwvndg5j">
        {/* Workforce Development */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-523" data-mp="1fk83sy5cxxo0">
          <div className="flex items-center gap-4 mb-6" data-id="element-524" data-mp="wnqiayh3qu07">
            <div className="w-14 h-14 rounded-xl bg-teal flex items-center justify-center text-white shadow-lg" data-id="element-525" data-mp="wo8kscucy4gk">
              <Briefcase size={28} data-id="element-526" data-mp="rqgluyvapi2v" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-527" data-mp="16m68mz8b1z8n">
              Workforce Development and Role Readiness
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-528" data-mp="11p61sm3mssgh">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-529" data-mp="2i35ylo76k1zi">
              SyNeural can help employers better understand the non-technical
              capabilities that shape role readiness and long-term performance.
              These include reasoning quality, self-direction, emotional
              regulation, communication, execution discipline, and consequential
              thinking. By identifying where those capabilities are strong or
              underdeveloped, the platform can help organizations support
              employees more effectively and prepare them for broader
              responsibilities.
            </p>
          </div>
        </motion.div>

        {/* Leadership Development */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-530" data-mp="1f9sd907axyqp">
          <div className="flex items-center gap-4 mb-6" data-id="element-531" data-mp="1t8s2oa2n2hnx">
            <div className="w-14 h-14 rounded-xl bg-navy-light flex items-center justify-center text-white shadow-lg" data-id="element-532" data-mp="1t776kg0oeqny">
              <Award size={28} data-id="element-533" data-mp="1ye5arb8r7eg2" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-534" data-mp="1yddq2vgp314k">
              Leadership Development
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-535" data-mp="1pdgue9vw1200">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-536" data-mp="1pftlpdnjcopq">
              A major use of the platform in employer settings is leadership
              development. As people move into roles with more responsibility,
              judgment matters more. SyNeural can support the development of
              stronger managers, team leads, and executives by focusing on
              capabilities such as decision quality, systems thinking,
              communication under pressure, foresight, responsibility, and the
              ability to handle complexity without destabilizing teams or
              institutions.
            </p>
          </div>
        </motion.div>

        {/* Advancement and Internal Mobility */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-537" data-mp="135l6gh5kwfks">
          <div className="flex items-center gap-4 mb-6" data-id="element-538" data-mp="ff7ndg4z1r2c">
            <div className="w-14 h-14 rounded-xl bg-green-soft flex items-center justify-center text-white shadow-lg" data-id="element-539" data-mp="fhtw3j3lmcm5">
              <TrendingUp size={28} data-id="element-540" data-mp="5hjfdoicun28" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-541" data-mp="5iung0iu2gz5">
              Advancement and Internal Mobility
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-542" data-mp="1zusvw09bj8tz">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-543" data-mp="24u5eu9x2jvra">
              Organizations often struggle to identify who is ready for growth
              and what specific areas need strengthening before advancement.
              SyNeural helps make this process more structured by showing where
              employees have strong potential, where capability bottlenecks
              exist, and what development pathways could support greater
              readiness. This makes the platform useful not only for assessment,
              but for internal mobility, progression planning, and long-term
              talent cultivation.
            </p>
          </div>
        </motion.div>

        {/* Execution and Team Performance */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-544" data-mp="lv7ihpbb6y1f">
          <div className="flex items-center gap-4 mb-6" data-id="element-545" data-mp="2ipu7ps5dwccl">
            <div className="w-14 h-14 rounded-xl bg-navy flex items-center justify-center text-white shadow-lg" data-id="element-546" data-mp="1wvltb0u7u1t0">
              <Zap size={28} data-id="element-547" data-mp="21svhkd5vc7f9" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-548" data-mp="21rkdsdcfazo5">
              Execution, Communication, and Team Performance
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-549" data-mp="2bj6x6wyx051z">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-550" data-mp="1dx9hf7bx6dtq">
              Many organizational bottlenecks are driven not by lack of
              intelligence, but by weak execution, poor planning, communication
              breakdowns, reactivity, or limited judgment under pressure.
              SyNeural can help employers better understand these issues and
              connect them to practical support pathways. This can improve team
              dynamics, reliability, role fit, and broader organizational
              performance.
            </p>
          </div>
        </motion.div>

        {/* Beyond Traditional Evaluation */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-551" data-mp="nlz1dpkedkkp">
          <div className="flex items-center gap-4 mb-6" data-id="element-552" data-mp="8wt8ejc5kql9">
            <div className="w-14 h-14 rounded-xl bg-teal flex items-center justify-center text-white shadow-lg" data-id="element-553" data-mp="8ye1e4itk1h0">
              <Target size={28} data-id="element-554" data-mp="4xu3uzuc2vuc" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-555" data-mp="3w2tvogn4pwn6">
              Employee Development Beyond Traditional Evaluation
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-556" data-mp="1ua5nrtzjil12">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-557" data-mp="1pbb64vdb72ij">
              Traditional performance reviews often capture outcomes without
              clearly explaining the underlying capabilities that produced them.
              SyNeural adds a deeper layer by helping organizations understand
              how employees think, how they respond to pressure, where they may
              be vulnerable to poor judgment, and where structured development
              could improve outcomes. This creates a more useful framework for
              coaching, support, and progression.
            </p>
          </div>
        </motion.div>

        {/* Organizational Resilience */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-558" data-mp="3frpdvp8759z9">
          <div className="flex items-center gap-4 mb-6" data-id="element-559" data-mp="2x30u56lg9ap2">
            <div className="w-14 h-14 rounded-xl bg-navy-light flex items-center justify-center text-white shadow-lg" data-id="element-560" data-mp="2x2j10iu33cld">
              <Building2 size={28} data-id="element-561" data-mp="2mx0zys5vjsm4" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-562" data-mp="2rqvruivmvdwa">
              Organizational Resilience and Institutional Strength
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-563" data-mp="2hzrbbeqb7kzc">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-564" data-mp="35plndu2t15ij">
              Organizations become stronger when they are staffed by people who
              can think clearly, adapt to complexity, and carry responsibility
              well. SyNeural supports this by helping employers strengthen the
              human layer beneath systems, processes, and leadership structures.
              That makes it relevant not only for individual development, but
              for organizational resilience, succession planning, and long-term
              institutional capability.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Why It Matters */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-navy-deep text-white" data-id="element-565" data-mp="3s03mrxj4krl9">
        <div className="max-w-4xl mx-auto" data-id="element-566" data-mp="3mzfrv0rrzqlr">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-567" data-mp="3i7xv6owscc6l">
            <h2 className="text-3xl font-heading font-bold mb-6" data-id="element-568" data-mp="347mxp30yzzcg">
              Why It Matters for Employers
            </h2>
            <p className="text-xl leading-relaxed opacity-90" data-id="element-569" data-mp="2zbfv9k7s95z9">
              Employers need more than talent. They need people who can convert
              talent into judgment, judgment into execution, and execution into
              reliable contribution. SyNeural helps create a more structured way
              to understand and develop those capabilities. It supports stronger
              teams, more capable leadership, and a clearer path from potential
              to readiness across the workforce.
            </p>
          </motion.div>
        </div>
      </section>
    </div>;
}